import { createFileRoute } from '@tanstack/react-router'
import type {
  InvestorIndexPublishResultData,
  Options,
  V2AdminPublishInvestorIndexData,
} from '@workspace/api-backend'
import { LARGE_DEPUBLISH_ERROR_CODE } from '@workspace/seedforge-shared'
import { Callout } from '@workspace/ui/components/callout'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { Dropzone } from '@workspace/ui/components/dropzone'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { MetricCard } from '@workspace/ui/components/metric-card'
import { Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import {
  useAdminInvestorIndexStatusQuery,
  useAdminPublishInvestorIndexMutation,
} from '../../../features/admin/hooks/use-admin-api'
import { FeedPreflightCard } from '../../../features/investor-index/components/feed-preflight-card'
import { PublicationHistory } from '../../../features/investor-index/components/publication-history'
import {
  type FeedPreflight,
  preflightFeed,
} from '../../../features/investor-index/lib/feed-preflight'
import { formatDateTime } from '../../../features/investor-index/lib/format'
import { apiErrorMessage } from '../../../lib/api-error'

export const Route = createFileRoute('/admin/investor-index/')({
  component: AdminInvestorIndexPage,
})

/**
 * The backend refuses a feed that would de-publish more than a tenth of the
 * live pages until the admin confirms it; that refusal carries a stable code,
 * anything else is an ordinary error.
 */
function isLargeDepublishRefusal(error: unknown): boolean {
  const body = error as { error?: { code?: unknown } } | null
  return body?.error?.code === LARGE_DEPUBLISH_ERROR_CODE
}

/** Mirrors the route's body limit; a bigger file is refused before it is read. */
const MAX_FEED_BYTES = 32 * 1024 * 1024

function AdminInvestorIndexPage() {
  const statusQuery = useAdminInvestorIndexStatusQuery()
  const publishMutation = useAdminPublishInvestorIndexMutation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [preflight, setPreflight] = useState<FeedPreflight | null>(null)
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null)
  const [result, setResult] = useState<InvestorIndexPublishResultData | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const status = statusQuery.data?.data
  const latest = status?.latestPublication ?? null
  const draftPages = status ? Object.values(status.draft).reduce((sum, n) => sum + n, 0) : 0

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setResult(null)
    setFileError(null)
    publishMutation.reset()
    if (file.size > MAX_FEED_BYTES) {
      setFileError(
        `${file.name} is ${Math.round(file.size / 1024 / 1024)} MB; the limit is ${MAX_FEED_BYTES / 1024 / 1024} MB.`
      )
      return
    }
    try {
      const text = await file.text()
      setFileName(file.name)
      setPreflight(preflightFeed(text))
    } catch {
      setFileError(`${file.name} could not be read.`)
    }
  }

  const clearFile = () => {
    setFileName(null)
    setPreflight(null)
    setFileError(null)
    setConfirmMessage(null)
    publishMutation.reset()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const publish = (confirmLargeDepublish: boolean) => {
    if (!preflight?.feed) return
    setConfirmMessage(null)
    publishMutation.mutate(
      {
        body: { feed: preflight.feed, confirmLargeDepublish },
      } as Options<V2AdminPublishInvestorIndexData>,
      {
        onSuccess: (response) => {
          if (response.status === 'ok') {
            setResult(response.data)
            clearFile()
          }
        },
        onError: (error) => {
          if (isLargeDepublishRefusal(error)) {
            setConfirmMessage(apiErrorMessage(error, 'This feed would de-publish live pages.'))
          }
        },
      }
    )
  }

  const publishError =
    publishMutation.error && !confirmMessage
      ? apiErrorMessage(publishMutation.error, 'Publishing failed. Please try again.')
      : null

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 py-2">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Investor Index</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The public Active Investor Index is rebuilt from the certified weekly feed. Upload the
          feed JSON and publish; pages missing from a new feed are de-published, never deleted.
        </p>
      </div>

      {statusQuery.isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <MetricCard label="List pages live" value={status?.published.list ?? 0} />
            <MetricCard label="Fund sheets live" value={status?.published.fund ?? 0} />
            <MetricCard
              label="Public rows"
              value={latest?.publicRows ?? 0}
              subtitle="in the last published feed"
            />
            <MetricCard label="Draft pages" value={draftPages} subtitle="de-published, kept" />
          </div>

          {latest ? (
            <Callout tone={latest.certificationStatus === 'CERTIFIED' ? 'good' : 'warn'}>
              Live feed certified {latest.certificationDate} ({latest.certificationStatus}), pool{' '}
              <span className="font-mono">{latest.poolSha256.slice(0, 12)}</span>, published{' '}
              {formatDateTime(latest.createdAt)}
              {latest.publishedByName ? ` by ${latest.publishedByName}` : ''}.
            </Callout>
          ) : (
            <Callout tone="warn">
              Nothing has been published yet. The public pages 404 until then.
            </Callout>
          )}
        </>
      )}

      <div className="space-y-4 rounded-2xl border border-border p-5">
        <div>
          <h2 className="text-base font-semibold text-foreground">Publish a feed</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick the <span className="font-mono">latest.json</span> written by the weekly
            investor-extraction routine. Every gate is checked here first and again on the server.
          </p>
        </div>

        {fileName && preflight ? (
          <div className="space-y-4">
            <FeedPreflightCard fileName={fileName} preflight={preflight} />
            <div className="flex flex-wrap items-center gap-2">
              <CtaButton
                disabled={!preflight.ok}
                loading={publishMutation.isPending}
                onClick={() => publish(false)}
              >
                Publish {preflight.ok ? `${preflight.feed?.pages.length ?? 0} pages` : ''}
              </CtaButton>
              <SecondaryButton size="md" icon={<Trash2 />} onClick={clearFile}>
                Choose another file
              </SecondaryButton>
            </div>
          </div>
        ) : (
          <Dropzone
            title="Choose the feed JSON"
            hint="latest.json from the weekly investor-extraction routine, up to 32 MB"
            error={fileError}
            className="cursor-pointer"
            rootProps={{
              role: 'button',
              tabIndex: 0,
              onClick: () => fileInputRef.current?.click(),
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click()
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFile}
              className="hidden"
            />
          </Dropzone>
        )}

        {publishError && <ErrorBanner message={publishError} onDismiss={publishMutation.reset} />}

        {result && (
          <Callout tone="good">
            Published: {result.upserted} pages upserted, {result.unchanged} unchanged,{' '}
            {result.depublished} de-published.
            {result.depublishedPageKeys.length > 0 && (
              <span className="font-mono text-xs">
                {' '}
                ({result.depublishedPageKeys.slice(0, 8).join(', ')}
                {result.depublishedPageKeys.length > 8 ? ', …' : ''})
              </span>
            )}
          </Callout>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Recent publications</h2>
        <PublicationHistory
          publications={status?.recentPublications ?? []}
          isLoading={statusQuery.isLoading}
        />
      </div>

      <Dialog
        open={confirmMessage !== null}
        onOpenChange={(open) => !open && setConfirmMessage(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Large de-publish</DialogTitle>
            <DialogDescription>{confirmMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <SecondaryButton size="md" onClick={() => setConfirmMessage(null)}>
              Cancel
            </SecondaryButton>
            <CtaButton loading={publishMutation.isPending} onClick={() => publish(true)}>
              Publish anyway
            </CtaButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
